import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Drawer, Input, Spin, Tooltip, message } from "antd";
import {
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined
} from "@ant-design/icons";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import CiqAgGrid from "components/ciq-ag-grid";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import { EUserRoleName } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  formatLodLabel,
  type DsmScopeItem,
  type Lod,
  type ScopeItemInput
} from "design-scope";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import type { DesignScopeListItem } from "mock-data/design-scope";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import {
  PRINT_VIEW_STORAGE_KEY,
  type DesignScopeLinkedScopePrintViewState
} from "pages/print-view-layout/model";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { DateUtils } from "utils/dateutils";
import {
  defaultLodIdFromOptions,
  lodForOptionId,
  lodOptionHeading,
  lodRankFromOption,
  useLevelOfDetail,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import { useDesignScopeList } from "../hooks/use-design-scope-list";
import {
  buildMasterformatFilter,
  buildUniformatFilter,
  ClassificationFilterMenu,
  masterformatDivisionId
} from "./classification-filter-menu";
import type { ScopeContext } from "./scope-context";
import { toDesignScopeUpdateInstance } from "./design-scope-create-mappers";
import {
  filterDesignScopeList,
  lodFromApiValue
} from "./design-scope-list-filter";
import { toModalScopeItem } from "./design-scope-list-mappers";
import { classificationCell, classParts, scopeTitle } from "./scope-list-cells";
import { ManageScopeLinksPanel } from "./manage-scope-links-panel";
import {
  designScopeLinkedScopeExportEventType,
  designScopeLinkedScopeFileBaseName,
  SCOPE_LIST_EXPORT_EXCLUDED_COLUMNS
} from "./scope-list-export-audit";
import type { ScopeListPrintRow } from "./scope-list-print-columns";

function toInput(item: DsmScopeItem): ScopeItemInput {
  return {
    classification: item.classification,
    title: item.title,
    description: item.description,
    status: item.status,
    responsibility: item.responsibility,
    notes: item.notes,
    commitments: item.commitments,
    bids: item.bids
  };
}

type LinkContext = {
  bidPackageId?: string;
  designPackageId?: string;
  milestoneId?: string;
};

/** Attach this milestone / bid package so an update links without rewriting the item. */
function withContextLink(
  item: DsmScopeItem,
  ctx: LinkContext,
  defaultLodId: string,
  defaultLod: Lod = 1
): DsmScopeItem {
  if (ctx.designPackageId && ctx.milestoneId) {
    const already = item.commitments.some(
      (c) =>
        c.designPackageId === ctx.designPackageId &&
        c.milestoneId === ctx.milestoneId
    );
    if (already) return item;
    return {
      ...item,
      commitments: [
        ...item.commitments,
        {
          designPackageId: ctx.designPackageId,
          milestoneId: ctx.milestoneId,
          lod: defaultLod,
          levelOfDetailValueId: defaultLodId,
          notes: ""
        }
      ]
    };
  }
  if (ctx.bidPackageId) {
    if (item.bids.some((b) => b.bidPackageId === ctx.bidPackageId)) return item;
    return {
      ...item,
      bids: [
        ...item.bids,
        {
          bidPackageId: ctx.bidPackageId,
          role: "",
          primary: item.bids.length === 0
        }
      ]
    };
  }
  return item;
}

function missingFromMilestone(
  it: DsmScopeItem,
  designPackageId?: string,
  milestoneId?: string
): boolean {
  if (!designPackageId || !milestoneId) return false;
  return !it.commitments.some(
    (c) =>
      c.designPackageId === designPackageId && c.milestoneId === milestoneId
  );
}

type BidGroup = { id: string; label: string };

type ScopeGridRow = {
  id: string;
  itemId: string;
  title: string;
  uniformat: string;
  uniformatCode: string;
  uniformatTitle: string;
  uniformatCustom: boolean;
  masterformat: string;
  masterformatCode: string;
  masterformatTitle: string;
  masterformatCustom: boolean;
  masterformatDivision: string;
  loc: string;
  lod: Lod | null;
  lodId: string;
  lodValue: string;
  note: string;
  bidPackage: string;
  bids: BidGroup[];
};

function packageLink(
  item: DesignScopeListItem,
  designPackageId?: string,
  milestoneId?: string
) {
  const linked = item.design_packages.find(
    (link) =>
      link.design_package?.id === designPackageId &&
      link.design_package_milestone?.id === milestoneId
  );
  return linked ?? item.design_packages[0];
}

function locOf(
  item: DesignScopeListItem,
  options: LevelOfDetailOption[],
  designPackageId?: string,
  milestoneId?: string
): { label: string; lod: Lod | null; lodId: string; lodValue: string } {
  const detail = packageLink(
    item,
    designPackageId,
    milestoneId
  )?.level_of_detail;
  const value = detail?.value ?? "";
  const lodId = detail?.id == null ? "" : String(detail.id);
  if (!value && !lodId)
    return { label: "", lod: null, lodId: "", lodValue: "" };
  // Resolve against the project's LOD list by id (names are configurable, so
  // matching the API value against fixed labels mislabels most levels).
  const index = lodId ? options.findIndex((option) => option.id === lodId) : -1;
  if (index >= 0) {
    const option = options[index]!;
    const heading = lodOptionHeading(option, index);
    const name = option.label.trim();
    return {
      label: name ? `${heading} ${name}` : heading,
      lod: lodRankFromOption(option, index),
      lodId,
      lodValue: value
    };
  }
  if (!value) return { label: "", lod: null, lodId, lodValue: "" };
  const lod = lodFromApiValue(value);
  return {
    label: options.length ? value : formatLodLabel(lod),
    lod,
    lodId,
    lodValue: value
  };
}

function lodHoverTitle(
  row: ScopeGridRow | undefined,
  options: LevelOfDetailOption[]
): string | undefined {
  if (!row?.loc) return undefined;
  const byId = row.lodId
    ? options.find((option) => option.id === row.lodId)
    : undefined;
  const match =
    byId ??
    (row.lodValue
      ? options.find(
          (option) =>
            option.label.trim().toLowerCase() ===
            row.lodValue.trim().toLowerCase()
        )
      : undefined);
  const description = match?.description?.trim();
  if (!description) return row.loc;
  return `${row.loc} - ${description}`;
}

function noteOf(
  item: DesignScopeListItem,
  designPackageId?: string,
  milestoneId?: string
): string {
  if (item.internal_notes) return item.internal_notes;
  return packageLink(item, designPackageId, milestoneId)?.notes ?? "";
}

function bidGroupsOf(item: DesignScopeListItem): BidGroup[] {
  if (!item.bid_packages.length) {
    return [{ id: "", label: "No Bid Package" }];
  }
  return item.bid_packages.map((bid) => ({
    id: bid.id,
    label: [bid.number, bid.title].filter(Boolean).join(" ") || "Bid Package"
  }));
}

function primaryBidLabel(
  groups: BidGroup[],
  item: DesignScopeListItem
): string {
  const primary = item.bid_packages.find((bid) => bid.is_primary);
  if (!primary) return groups[0]?.label ?? "No Bid Package";
  return (
    groups.find((group) => group.id === primary.id)?.label ??
    groups[0]?.label ??
    "No Bid Package"
  );
}

function toGridRow(
  item: DesignScopeListItem,
  lodOptions: LevelOfDetailOption[],
  designPackageId?: string,
  milestoneId?: string
): ScopeGridRow {
  const uniformat = classParts(item, "uni-format");
  const masterformat = classParts(item, "master-format");
  const loc = locOf(item, lodOptions, designPackageId, milestoneId);
  const bids = bidGroupsOf(item);
  return {
    id: item.id,
    itemId: item.id,
    title: scopeTitle(item),
    uniformat: uniformat.label,
    uniformatCode: uniformat.code,
    uniformatTitle: uniformat.title,
    uniformatCustom: uniformat.custom,
    masterformat: masterformat.label,
    masterformatCode: masterformat.code,
    masterformatTitle: masterformat.title,
    masterformatCustom: masterformat.custom,
    masterformatDivision: masterformatDivisionId(item),
    loc: loc.label,
    lod: loc.lod,
    lodId: loc.lodId,
    lodValue: loc.lodValue,
    note: noteOf(item, designPackageId, milestoneId),
    bidPackage: primaryBidLabel(bids, item),
    bids
  };
}

function expandByBid(rows: ScopeGridRow[]): ScopeGridRow[] {
  return rows.flatMap((row) =>
    row.bids.map((bid) => ({
      ...row,
      id: `${row.itemId}:${bid.id || "none"}`,
      bidPackage: bid.label
    }))
  );
}

function formatScopeDate(value: string | null | undefined) {
  if (!value) return "—";
  return DateUtils.format(value) || "—";
}

function ScopeDateReadout(props: {
  testId: string;
  label: string;
  value: string | null | undefined;
}) {
  const { testId, label, value } = props;
  return (
    <div className="min-w-[88px] space-y-2" data-testid={testId}>
      <div className="text-xs font-normal leading-4 text-ciq-black-a38">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-ciq-black-a88">
        {formatScopeDate(value)}
      </div>
    </div>
  );
}

function BidPackageGroupLabel(params: ICellRendererParams<ScopeGridRow>) {
  const count = params.node.allChildrenCount ?? 0;
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span className="truncate">{params.value}</span>
      <span className="rounded-full border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs font-normal text-ciq-black-a45">
        {count}
      </span>
    </span>
  );
}

export function ScopeListModal({
  open,
  projectId,
  context,
  onClose,
  onChanged
}: {
  open: boolean;
  projectId: string;
  context: ScopeContext | null;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const {
    projectToken,
    tokenRetrievalState,
    isProjectArchived,
    tokenContents,
    projectDetails,
    gqlClientForProject,
    isFeatureFlagEnabled
  } = useContext(ProjectContext);
  const token = projectToken || tokenRetrievalState?.token;
  const { options: lodOptions } = useLevelOfDetail(projectId);
  const { items: allListItems, loading, reload } = useDesignScopeList(token);
  const gridRef = useRef<AgGridReact<ScopeGridRow>>(null);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");
  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const [managing, setManaging] = useState(false);
  const [query, setQuery] = useState("");
  const [showMissing, setShowMissing] = useState(false);
  const [groupByBid, setGroupByBid] = useState(false);
  const [uniformat, setUniformat] = useState<string[]>([]);
  const [masterformat, setMasterformat] = useState<string[]>([]);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addingAll, setAddingAll] = useState(false);

  const bidPackageId =
    context?.kind === "bid" ? context.bidPackageId : undefined;
  const designPackageId = context?.designPackageId;
  const milestoneId = context?.milestoneId;

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const listItems = useMemo(() => {
    if (bidPackageId) {
      return filterDesignScopeList(allListItems, { bidPackageId });
    }
    if (designPackageId && milestoneId) {
      return filterDesignScopeList(allListItems, {
        designPackageId,
        milestoneId
      });
    }
    return [] as DesignScopeListItem[];
  }, [allListItems, bidPackageId, designPackageId, milestoneId]);

  const items = useMemo(() => listItems.map(toModalScopeItem), [listItems]);

  const defaultLodId = useMemo(
    () => defaultLodIdFromOptions(lodOptions),
    [lodOptions]
  );
  const linkCtx: LinkContext = {
    bidPackageId,
    designPackageId,
    milestoneId
  };

  useEffect(() => {
    setManaging(false);
    setQuery("");
    setShowMissing(false);
    setGroupByBid(false);
    setUniformat([]);
    setMasterformat([]);
  }, [open, bidPackageId, designPackageId, milestoneId]);

  const canManageLinks =
    context?.kind === "milestone" &&
    tokenContents?.role === EUserRoleName[EUserRoleName.gc_project_admin] &&
    !isProjectArchived;

  const canShowMissing =
    context?.kind === "bid" && Boolean(designPackageId && milestoneId);
  const missingActive = canShowMissing && showMissing;

  const sourceItems = useMemo(() => {
    const missingIds = new Set(
      items
        .filter((it) => missingFromMilestone(it, designPackageId, milestoneId))
        .map((it) => it.id)
    );
    return listItems.filter(
      (item) => !missingActive || missingIds.has(item.id)
    );
  }, [items, listItems, missingActive, designPackageId, milestoneId]);

  const sourceRows = useMemo(
    () =>
      sourceItems.map((item) =>
        toGridRow(item, lodOptions, designPackageId, milestoneId)
      ),
    [sourceItems, lodOptions, designPackageId, milestoneId]
  );
  const uniformatFilter = useMemo(
    () => buildUniformatFilter(sourceItems),
    [sourceItems]
  );
  const masterformatFilter = useMemo(
    () => buildMasterformatFilter(sourceItems),
    [sourceItems]
  );

  const displayedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sourceRows.filter((row) => {
      if (uniformat.length && !uniformat.includes(row.uniformatCode)) {
        return false;
      }
      if (
        masterformat.length &&
        !masterformat.includes(row.masterformatDivision)
      ) {
        return false;
      }
      if (!q) return true;
      const hay = [
        row.title,
        row.uniformat,
        row.masterformat,
        row.loc,
        row.note,
        row.bidPackage
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sourceRows, query, uniformat, masterformat]);

  const gridRows = useMemo(
    () => (groupByBid ? expandByBid(displayedRows) : displayedRows),
    [displayedRows, groupByBid]
  );

  const scopeCount = new Set(displayedRows.map((row) => row.itemId)).size;

  const addToLinkedMilestone = async (ids: string[]) => {
    if (!token || !designPackageId || !milestoneId || ids.length === 0) return;
    if (ids.length === 1) setAddingId(ids[0]!);
    else setAddingAll(true);
    try {
      for (const itemId of ids) {
        const row = allListItems.find((i) => i.id === itemId);
        if (!row) continue;
        const input = toInput(
          withContextLink(
            toModalScopeItem(row),
            linkCtx,
            defaultLodId,
            lodForOptionId(lodOptions, defaultLodId)
          )
        );
        const response = await updateDesignScopeInstances(
          {
            feature_type: "DESIGN_SCOPE",
            feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
            feature_instances: [
              toDesignScopeUpdateInstance(input, itemId, {
                includeClassification: false
              })
            ]
          },
          token
        );
        if (!response.success) {
          message.error(response.errorMsg || "Unable to update scope item.");
          return;
        }
      }
      message.success(
        ids.length === 1
          ? "Scope item added to milestone"
          : "Scope items added to milestone"
      );
      await reload({ silent: true });
      onChanged?.();
    } finally {
      setAddingId(null);
      setAddingAll(false);
    }
  };

  const columnDefs = useMemo<ColDef<ScopeGridRow>[]>(() => {
    const columns: ColDef<ScopeGridRow>[] = [
      ...(groupByBid
        ? [
            {
              colId: "bidPackage",
              field: "bidPackage",
              headerName: "Bid Package",
              rowGroup: true,
              hide: true
            } satisfies ColDef<ScopeGridRow>
          ]
        : []),
      {
        colId: "title",
        field: "title",
        headerName: "Title",
        minWidth: 180,
        flex: 1.1,
        filter: false
      },
      {
        colId: "uniformat",
        field: "uniformat",
        headerName: "UniFormat",
        minWidth: 200,
        flex: 1.4,
        cellRenderer: (p: { data?: ScopeGridRow }) =>
          classificationCell(
            "UF",
            p.data?.uniformatCode ?? "",
            p.data?.uniformatTitle ?? "",
            p.data?.uniformatCustom
          )
      },
      {
        colId: "masterformat",
        field: "masterformat",
        headerName: "MasterFormat",
        minWidth: 200,
        flex: 1.4,
        cellRenderer: (p: { data?: ScopeGridRow }) =>
          classificationCell(
            "MF",
            p.data?.masterformatCode ?? "",
            p.data?.masterformatTitle ?? "",
            p.data?.masterformatCustom
          )
      }
    ];
    if (context?.kind === "milestone") {
      columns.push(
        {
          colId: "loc",
          field: "loc",
          headerName: "LOD",
          minWidth: 180,
          flex: 1.2,
          filter: false,
          cellRenderer: (p: { data?: ScopeGridRow }) => {
            const label = p.data?.loc;
            if (!label) return "—";
            return (
              <span
                className="block truncate"
                title={lodHoverTitle(p.data, lodOptions)}
              >
                {label}
              </span>
            );
          }
        },
        {
          colId: "note",
          field: "note",
          headerName: "Note",
          minWidth: 180,
          flex: 1.3,
          filter: false,
          cellRenderer: (p: { data?: ScopeGridRow }) => {
            const note = p.data?.note;
            if (!note) return "—";
            return (
              <span className="block truncate" title={note}>
                {note}
              </span>
            );
          }
        }
      );
    }
    if (missingActive && !isProjectArchived) {
      columns.push({
        colId: "add",
        headerName: "",
        width: 96,
        flex: 0,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        cellRenderer: (p: { data?: ScopeGridRow }) =>
          p.data ? (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              className="!px-0"
              data-testid={`scope-list-add-${p.data.itemId}`}
              loading={addingId === p.data.itemId || addingAll}
              onClick={() => void addToLinkedMilestone([p.data!.itemId])}
            >
              Add
            </Button>
          ) : null
      });
    }
    return columns;
    // addToLinkedMilestone closes over the latest token and list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context?.kind,
    groupByBid,
    lodOptions,
    missingActive,
    addingId,
    addingAll,
    isProjectArchived
  ]);

  const milestoneLabel =
    context?.kind === "bid" && context.milestoneName
      ? context.milestoneName
      : "the linked milestone";
  const missingIds = items
    .filter((it) => missingFromMilestone(it, designPackageId, milestoneId))
    .map((it) => it.id);

  const drawerTitle =
    context?.kind === "milestone" ? (
      <div>
        <div className="text-xs font-normal text-ciq-planner-ink4">
          {context.designPackageTitle || context.label}
        </div>
        {context.milestoneName ? (
          <div className="text-lg font-semibold text-ciq-black">
            {context.milestoneName}
          </div>
        ) : null}
      </div>
    ) : (
      <div className="text-lg font-semibold text-ciq-black">
        {context?.label ?? "Scope items"}
      </div>
    );

  const exportFileBaseName = designScopeLinkedScopeFileBaseName({
    designPackageTitle:
      context?.kind === "milestone"
        ? context.designPackageTitle
        : context?.kind === "bid"
          ? context.designPackageName
          : undefined,
    milestoneName:
      context?.kind === "milestone"
        ? context.milestoneName
        : context?.kind === "bid"
          ? context.milestoneName
          : undefined,
    label: context?.label,
    projectName: projectDetails?.name
  });

  const onExportCsv = () => {
    if (!gridRef.current) return;
    const filename = `${exportFileBaseName}.csv`;
    exportEntityLogAsCSV({
      filename,
      gridRef: { api: gridRef.current.api },
      includedColumns: [],
      excludedColumns: SCOPE_LIST_EXPORT_EXCLUDED_COLUMNS
    });
    saveAuditLogEvent({
      eventType: designScopeLinkedScopeExportEventType("CSV"),
      featureType: "DESIGN_SCOPE",
      filename
    });
  };

  const onExportExcel = (options: ExcelExportOptions) => {
    if (!gridRef.current || !projectDetails) return;
    const filename = `${sanitizeSheetName(exportFileBaseName)}.xlsx`;
    const scopeLabel =
      context?.kind === "milestone"
        ? context.milestoneName || context.label
        : context?.label || "Scope Items";
    exportEntityLogAsExcel({
      projectDetails,
      gridRef,
      filename,
      prependText: `${
        projectDetails.name
      } - ${scopeLabel} - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
        `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
      )}`,
      excludedColumns: SCOPE_LIST_EXPORT_EXCLUDED_COLUMNS,
      options
    });
    saveAuditLogEvent({
      eventType: designScopeLinkedScopeExportEventType("EXCEL"),
      featureType: "DESIGN_SCOPE",
      filename
    });
  };

  const onOpenPrintView = () => {
    if (!gridRef.current) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      sessionStorage.clear();
      const printRows: ScopeListPrintRow[] = gridRows.map((row) => ({
        id: row.id,
        title: row.title,
        uniformat: row.uniformat,
        uniformatCode: row.uniformatCode,
        uniformatTitle: row.uniformatTitle,
        uniformatCustom: row.uniformatCustom,
        masterformat: row.masterformat,
        masterformatCode: row.masterformatCode,
        masterformatTitle: row.masterformatTitle,
        masterformatCustom: row.masterformatCustom,
        loc: row.loc,
        note: row.note,
        bidPackage: row.bidPackage
      }));
      const scopeLabel =
        context?.kind === "milestone"
          ? context.milestoneName || context.label
          : context?.label || "Scope Items";
      const printViewState: DesignScopeLinkedScopePrintViewState = {
        pageId: "design-scope-linked-scope",
        title: `${
          projectDetails?.name || "Project"
        } - ${scopeLabel} - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState: gridRef.current.api.getState(),
        context: { rowData: printRows },
        skipColumnIds: SCOPE_LIST_EXPORT_EXCLUDED_COLUMNS,
        headerHeight: gridRef.current.props?.headerHeight,
        rowHeight: undefined,
        includeLodAndNote: context?.kind === "milestone",
        groupByBid,
        pdfFileBaseName: exportFileBaseName
      };
      sessionStorage.setItem(
        PRINT_VIEW_STORAGE_KEY,
        JSON.stringify(printViewState)
      );
      window.open(`/project/${projectId}/printView`, "_blank");
    } catch (error) {
      console.error("Error opening linked scope print view:", error);
      message.error("An error occurred while opening print view.");
    }
  };

  const openPrintValidation = () => {
    if (!gridRef.current) return;
    PrintViewValidationModal({
      gridApi: gridRef.current.api,
      onProceed: onOpenPrintView,
      skipColumnIds: SCOPE_LIST_EXPORT_EXCLUDED_COLUMNS
    });
  };

  const exportOptions: ExportFormatOption[] = [];
  exportOptions.push({
    key: "export_csv",
    title: "CSV",
    testId: "scope-list-export-csv"
  });
  if (isExcelExportEnabled) {
    exportOptions.push({
      key: "export_excel",
      title: "Excel (.xlsx)",
      isExcel: true,
      testId: "scope-list-export-excel"
    });
  }
  if (isPrintViewEnabled) {
    exportOptions.push({
      key: "print_view",
      title: "PDF/Print",
      testId: "scope-list-export-pdf"
    });
  }

  const onProceedExport = ({ key, excelOptions }: ExportSelection) => {
    if (key === "export_excel") {
      if (excelOptions) onExportExcel(excelOptions);
      return;
    }
    if (key === "print_view") {
      openPrintValidation();
      return;
    }
    onExportCsv();
  };

  const exportControl =
    exportOptions.length > 0 ? (
      <ExportOptionsPopover
        options={exportOptions}
        onProceed={onProceedExport}
        excelTableName={projectDetails?.name || context?.label || "Scope Items"}
        showIncludeComments={false}
        disabled={!!isProjectArchived || loading}
        buttonType="default"
        buttonTestId="scope-list-export-btn"
        // Keep the menu inside the drawer so picking CSV/Excel/PDF is not an
        // outside click that unmounts the drawer before export runs.
        getPopupContainer={(trigger) =>
          trigger.closest(".ant-drawer-body") ?? document.body
        }
      />
    ) : null;

  function scopeListDrawer() {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        placement="right"
        size={1080}
        destroyOnHidden
        closable={{ placement: "end" }}
        className="scope-list-drawer"
        data-testid="scope-list-drawer"
        title={drawerTitle}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%"
          }
        }}
      >
        <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
          {context?.kind === "bid" &&
          (context.designPackageName || context.milestoneName) ? (
            <div>
              <div className="mb-1 text-xs text-ciq-black-a65">
                Linked to Design Package
              </div>
              <div
                className="flex items-center gap-2"
                data-testid="scope-list-subheading"
              >
                {context.designPackageName ? (
                  <span className="text-sm font-semibold text-ciq-black-a65">
                    {context.designPackageName}
                  </span>
                ) : null}
                {context.milestoneName ? (
                  <span className="inline-flex items-center gap-1.5 rounded bg-ciq-planner-accent-bg px-2 py-0.5 text-xs font-semibold text-ciq-planner-accent">
                    <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ciq-planner-accent" />
                    {context.milestoneName}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {context?.kind === "milestone" ? (
            <div className="flex flex-col gap-3">
              <div className="w-fit rounded bg-ciq-neutral-25 px-3 py-2">
                <div className="grid grid-cols-3 gap-6">
                  <ScopeDateReadout
                    testId="scope-list-committed"
                    label="Committed Date"
                    value={context.committedDate}
                  />
                  <ScopeDateReadout
                    testId="scope-list-projected"
                    label="Projected Date"
                    value={context.projectedDate}
                  />
                  <ScopeDateReadout
                    testId="scope-list-actual"
                    label="Actual Date"
                    value={context.actualDate}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                {typeof context.linkedBidCount === "number" ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-sm text-ciq-black-a88"
                    data-testid="scope-list-linked-bids"
                  >
                    <LinkOutlined className="text-ciq-black-a50 hover:text-ciq-black-a80" />
                    Linked to{" "}
                    <span className="font-semibold">
                      {context.linkedBidCount}
                    </span>{" "}
                    bid package
                  </span>
                ) : null}
                <Checkbox
                  checked={groupByBid}
                  className="!text-sm !font-normal !text-ciq-gray-960"
                  data-testid="scope-list-group-bids"
                  onChange={(e) => setGroupByBid(e.target.checked)}
                >
                  Group by linked Bid Packages
                </Checkbox>
              </div>
            </div>
          ) : null}

          {canShowMissing ? (
            <div className="flex items-center gap-1.5">
              <Checkbox
                checked={showMissing}
                className="!text-sm !font-normal !text-ciq-gray-960"
                data-testid="scope-list-show-missing"
                onChange={(e) => setShowMissing(e.target.checked)}
              >
                Show missing in linked milestone
              </Checkbox>
              <Tooltip
                title={`The Bid package has ${items.length} scope; linked Milestone has ${Math.max(0, items.length - missingIds.length)} scope. Check to see ${missingIds.length} missing items.`}
              >
                <span className="inline-flex">
                  <InfoCircleOutlined className="text-ciq-black-a45 hover:text-ciq-black-a80" />
                </span>
              </Tooltip>
            </div>
          ) : null}

          {missingActive ? (
            <div
              className="text-xs text-black/45"
              data-testid="scope-list-missing-help"
            >
              These scope items are in {context?.label ?? "this Bid Package"}{" "}
              but not in {milestoneLabel}.
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ciq-black-a88">
              Scope Items
            </span>
            <span
              className="rounded-full border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs text-ciq-black-a45"
              data-testid="scope-list-count"
            >
              {scopeCount}
            </span>
            {missingActive ? (
              <Button
                icon={<PlusOutlined />}
                className="ml-auto"
                data-testid="scope-list-add-all"
                loading={addingAll}
                disabled={missingIds.length === 0 || !!isProjectArchived}
                onClick={() => void addToLinkedMilestone(missingIds)}
              >
                Add All to Milestone
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              allowClear
              prefix={<SearchOutlined className="text-black/25" />}
              placeholder="Search scope items"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-w-[200px] max-w-xs flex-1"
              data-testid="scope-list-search"
            />
            <ClassificationFilterMenu
              testId="scope-list-uniformat"
              placeholder="UniFormat"
              allLabel="All UniFormat codes"
              searchPlaceholder="Search UniFormat code or name"
              model={uniformatFilter}
              value={uniformat}
              onChange={setUniformat}
              chipClass="bg-ciq-geekblue-100 text-ciq-geekblue-600"
            />
            <ClassificationFilterMenu
              testId="scope-list-masterformat"
              placeholder="MasterFormat"
              allLabel="All MasterFormat codes"
              searchPlaceholder="Search MasterFormat code or name"
              model={masterformatFilter}
              value={masterformat}
              onChange={setMasterformat}
              showOptionCounts
              chipClass="bg-ciq-golden-purple-100 text-ciq-golden-purple-900"
            />
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {exportControl}
              {canManageLinks ? (
                <Button
                  type="primary"
                  data-testid="scope-list-manage-links"
                  disabled={loading}
                  onClick={() => setManaging(true)}
                >
                  Manage Links <LinkOutlined />
                </Button>
              ) : null}
            </div>
          </div>

          {loading && items.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <Spin />
            </div>
          ) : (
            <div className="scope-list-grid relative min-h-0 w-full flex-1 [&_.ag-root-wrapper]:h-full [&_.ag-root-wrapper]:w-full [&_.ag-theme-alpine]:h-full [&_.ag-theme-alpine]:w-full">
              <CiqAgGrid<ScopeGridRow>
                gridRef={gridRef}
                rowData={gridRows}
                columnDefs={columnDefs}
                getRowId={(params) => params.data.id}
                getRowStyle={(params) =>
                  params.node.group
                    ? { backgroundColor: "var(--ciq-geekblue-50)" }
                    : undefined
                }
                sideBar={false}
                disableResizingAutoWidth
                groupDisplayType="groupRows"
                groupDefaultExpanded={-1}
                groupRowRendererParams={{
                  suppressCount: true,
                  innerRenderer: BidPackageGroupLabel
                }}
                rowSelection={{
                  mode: "singleRow",
                  checkboxes: false,
                  enableClickSelection: false
                }}
                defaultColDef={{
                  sortable: true,
                  filter: "agSetColumnFilter",
                  resizable: true,
                  menuTabs: ["filterMenuTab", "generalMenuTab"],
                  flex: 1,
                  minWidth: 110
                }}
              />
            </div>
          )}
        </div>
      </Drawer>
    );
  }
  function manageLinksDrawer() {
    if (context?.kind !== "milestone") return null;
    return (
      <Drawer
        open={open}
        onClose={() => setManaging(false)}
        placement="right"
        size={1080}
        destroyOnHidden
        closable={false}
        maskClosable={false}
        keyboard={false}
        className="scope-list-drawer"
        data-testid="scope-list-drawer"
        title={null}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%",
            padding: 0
          }
        }}
      >
        <ManageScopeLinksPanel
          items={allListItems}
          designPackageId={context.designPackageId}
          milestoneId={context.milestoneId}
          designPackageTitle={context.designPackageTitle || context.label}
          milestoneName={context.milestoneName}
          lodOptions={lodOptions}
          token={token}
          onBack={() => setManaging(false)}
          onSaved={async () => {
            await reload({ silent: true });
            onChanged?.();
            setManaging(false);
          }}
        />
      </Drawer>
    );
  }

  return managing ? manageLinksDrawer() : scopeListDrawer();
}
